import React, { useEffect, useState } from 'react';
import { 
  Calendar, User, Users, Briefcase, CheckCircle, Clock, 
  Search, Plus, X, ChevronDown, ChevronUp, Filter, RefreshCw, ShieldAlert
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';

const MarkAbsenteeManagement = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Filters
  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);

  // Summary data
  const [summary, setSummary] = useState(null);
  const [overview, setOverview] = useState([]);

  // Mark Absent Modal
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1); // 1: Employee Search, 2: Marking
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeResults, setEmployeeResults] = useState([]);
  const [markingMode, setMarkingMode] = useState('calendar'); // 'calendar' or 'range'
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [applyBridgeRule, setApplyBridgeRule] = useState(true);
  const [attendanceData, setAttendanceData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedBulkIds, setSelectedBulkIds] = useState(new Set());
  const [reopeningId, setReopeningId] = useState(null);
  const [isCorrectionMode, setIsCorrectionMode] = useState(false);

  // Fetch departments
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Fetch summary when filters change
  useEffect(() => {
    if (departmentId !== null) {
      fetchSummary();
    }
  }, [payrollMonth, payrollYear, departmentId]);

  // Component-level permission check
  if (!hasPermission('leave:absence:summary') && !hasPermission('leave:absence:check')) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <ShieldAlert className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You do not have permission to view absentee management.</p>
        </div>
      </div>
    );
  }

  const fetchDepartments = async () => {
    try {
      const res = await axios.get('/v1/departments');
      setDepartments(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        payroll_month: payrollMonth,
        payroll_year: payrollYear
      };
      if (departmentId) params.department_id = departmentId;

      const res = await axios.get('/v1/mark-absentee/summary', { params });
      setSummary(res.data.summary);
      setOverview(res.data.overview);
      setSelectedBulkIds(new Set());
    } catch (e) {
      console.error(e);
      setError('Unable to load attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBulkSelect = (empId) => {
    const newSelected = new Set(selectedBulkIds);
    if (newSelected.has(empId)) {
      newSelected.delete(empId);
    } else {
      newSelected.add(empId);
    }
    setSelectedBulkIds(newSelected);
  };

  const pendingEmployees = overview.filter(emp => emp.status === 'Pending');
  const allPendingSelected = pendingEmployees.length > 0 && pendingEmployees.every(emp => selectedBulkIds.has(emp.employee_id));

  const handleSelectAllPending = () => {
    if (allPendingSelected) {
      const newSelected = new Set(selectedBulkIds);
      pendingEmployees.forEach(emp => newSelected.delete(emp.employee_id));
      setSelectedBulkIds(newSelected);
    } else {
      const newSelected = new Set(selectedBulkIds);
      pendingEmployees.forEach(emp => newSelected.add(emp.employee_id));
      setSelectedBulkIds(newSelected);
    }
  };

  const handleBulkComplete = async () => {
    if (selectedBulkIds.size === 0) return;
    if (!window.confirm("Mark attendance processing as complete for the selected employees?")) {
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await axios.post('/v1/leave/mark-absence/bulk-complete', {
        employee_ids: Array.from(selectedBulkIds),
        month: payrollMonth,
        year: payrollYear
      });
      setSuccessMsg(`Successfully marked ${res.data.completed} employee(s) as complete.`);
      setSelectedBulkIds(new Set());
      await fetchSummary();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to complete bulk attendance review.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSearch = async (query) => {
    setEmployeeSearch(query);
    if (query.length < 2) {
      setEmployeeResults([]);
      return;
    }

    try {
      const res = await axios.get('/v1/benfish-employees/search', {
        params: { query: query, limit: 10 }
      });
      setEmployeeResults(res.data.employees || res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenEditModal = async (emp) => {
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await axios.get(`/v1/mark-absentee/employee/${emp.employee_id}`, {
        params: {
          payroll_month: payrollMonth,
          payroll_year: payrollYear
        }
      });
      setAttendanceData(res.data);
      
      const existingDates = new Set();
      let existingReason = '';
      res.data.calendar_grid.forEach(day => {
        if (day.existing_absent) {
          if (day.existing_absent.status === 'MANUAL_ABSENT' || day.day_type === 'WORKING_DAY') {
            existingDates.add(day.date);
          }
          if (day.existing_absent.reason && !existingReason) {
            existingReason = day.existing_absent.reason;
          }
        }
      });
      setSelectedDates(existingDates);
      setSelectedEmployee({
        id: emp.employee_id,
        emp_code: emp.employee_code,
        first_name: res.data.employee_name
      });
      setReason(existingReason || '');
      setIsCorrectionMode(false);
      setModalStep(2);
      setShowModal(true);
    } catch (e) {
      console.error(e);
      setError('Unable to load attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReopenAttendance = async (emp) => {
    if (reopeningId) return;
    setReopeningId(emp.employee_id);
    setError('');
    setSuccessMsg('');
    try {
      const res = await axios.post('/v1/mark-absentee/reopen', {
        employee_id: emp.employee_id,
        payroll_month: payrollMonth,
        payroll_year: payrollYear,
        reason: 'Attendance reopened for correction'
      });

      setSuccessMsg(`Attendance for ${emp.employee_name} reopened for correction. Modify dates and save to complete review.`);

      // Update overview table to reflect status Pending
      setOverview(prev => prev.map(item => {
        if (item.employee_id === emp.employee_id) {
          return {
            ...item,
            status: 'Pending',
            can_change_attendance: false
          };
        }
        return item;
      }));

      // Update summary counts
      if (summary) {
        setSummary(prev => ({
          ...prev,
          completed_reviews: Math.max(0, (prev.completed_reviews || 1) - 1),
          pending_reviews: (prev.pending_reviews || 0) + 1
        }));
      }

      // Open modal in step 2 with existing dates preserved
      const data = res.data.attendance_data;
      setAttendanceData(data);

      const existingDates = new Set();
      let existingReason = '';
      data.calendar_grid.forEach(day => {
        if (day.existing_absent) {
          if (day.existing_absent.status === 'MANUAL_ABSENT' || day.day_type === 'WORKING_DAY') {
            existingDates.add(day.date);
          }
          if (day.existing_absent.reason && !existingReason) {
            existingReason = day.existing_absent.reason;
          }
        }
      });

      setSelectedDates(existingDates);
      setSelectedEmployee({
        id: emp.employee_id,
        emp_code: emp.employee_code,
        first_name: emp.employee_name
      });
      setReason(existingReason || 'Attendance correction');
      setRemarks('');
      setIsCorrectionMode(true);
      setModalStep(2);
      setShowModal(true);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to reopen attendance review.');
    } finally {
      setReopeningId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsCorrectionMode(false);
    setSelectedEmployee(null);
    setSelectedDates(new Set());
    setPreviewData(null);
    setReason('');
    setRemarks('');
    setRangeFrom('');
    setRangeTo('');
  };

  const handleSelectEmployee = async (employee) => {
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await axios.get(`/v1/mark-absentee/employee/${employee.id}`, {
        params: {
          payroll_month: payrollMonth,
          payroll_year: payrollYear
        }
      });
      setAttendanceData(res.data);
      
      const existingDates = new Set();
      res.data.calendar_grid.forEach(day => {
        if (day.existing_absent) {
          existingDates.add(day.date);
        }
      });
      setSelectedDates(existingDates);
      setSelectedEmployee({
        id: employee.id,
        emp_code: employee.emp_code,
        first_name: res.data.employee_name
      });
      setModalStep(2);
    } catch (e) {
      console.error(e);
      setError('Unable to load attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (day) => {
    if (!day.is_editable) return;
    
    const newSelected = new Set(selectedDates);
    if (newSelected.has(day.date)) {
      newSelected.delete(day.date);
    } else {
      newSelected.add(day.date);
    }
    setSelectedDates(newSelected);
    setPreviewData(null);
  };

  const handleDateRangeSelect = () => {
    if (!rangeFrom || !rangeTo) {
      setError('Please select both From and To dates');
      return;
    }

    if (rangeFrom > rangeTo) {
      setError('From date cannot be after To date');
      return;
    }

    if (!attendanceData || !attendanceData.calendar_grid) return;

    setError('');
    const newSelected = new Set(selectedDates);

    // Parse dates by components in local time at 12:00 noon to avoid timezone shifts and daylight savings transitions
    const [fromY, fromM, fromD] = rangeFrom.split('-').map(Number);
    const [toY, toM, toD] = rangeTo.split('-').map(Number);

    const current = new Date(fromY, fromM - 1, fromD, 12, 0, 0);
    const end = new Date(toY, toM - 1, toD, 12, 0, 0);

    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;

      const day = attendanceData.calendar_grid.find(d => d.date === dateStr);
      if (day && !day.is_on_leave && !attendanceData.is_locked) {
        // Working days within the range are selected as manual absent days.
        // Boundary dates (both From and To dates explicitly chosen by the user) are always included,
        // even if they fall on a week-off or holiday.
        // Intermediate week-offs/holidays are left unselected so that the bridge rule
        // (when enabled) dynamically classifies them as bridge days.
        const isBoundary = (dateStr === rangeFrom || dateStr === rangeTo);
        const isWorkingDay = (day.day_type === 'WORKING_DAY');

        if (isWorkingDay || isBoundary) {
          newSelected.add(dateStr);
        }
      }

      current.setDate(current.getDate() + 1);
    }

    setSelectedDates(newSelected);
    setPreviewData(null);
  };


  const handlePreview = async () => {
    if (selectedDates.size === 0) {
      setError('Please select at least one date');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/v1/mark-absentee/preview', {
        employee_id: selectedEmployee.id,
        attendance_from: attendanceData.attendance_from,
        attendance_to: attendanceData.attendance_to,
        selected_dates: Array.from(selectedDates),
        apply_bridge_rule: applyBridgeRule
      });

      setPreviewData({
        selected_dates: res.data.selected_dates,
        bridged_dates: res.data.bridged_dates,
        final_dates: res.data.final_dates,
        manual_days: res.data.manual_days,
        bridge_days: res.data.bridge_days,
        total_days: res.data.total_days
      });
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to compute sandwich rule preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee');
      return;
    }

    if (selectedDates.size > 0 && !reason) {
      setError('Please provide a reason for marking absent');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        employee_id: selectedEmployee.id,
        attendance_from: attendanceData.attendance_from,
        attendance_to: attendanceData.attendance_to,
        selected_dates: Array.from(selectedDates),
        apply_bridge_rule: applyBridgeRule,
        reason: selectedDates.size > 0 ? reason : "Reviewed present",
        remarks: remarks || "",
        department_id: departmentId ? parseInt(departmentId) : null
      };

      const res = await axios.post('/v1/mark-absentee/save', payload);

      setSuccessMsg(`Successfully saved attendance review for ${res.data.employee_name}`);
      setShowModal(false);
      setIsCorrectionMode(false);
      setModalStep(1);
      setSelectedEmployee(null);
      setSelectedDates(new Set());
      setPreviewData(null);
      setReason('');
      setRemarks('');
      setRangeFrom('');
      setRangeTo('');

      // Update local row
      setOverview(prev => prev.map(emp => {
        if (emp.employee_id === res.data.employee_id) {
          return {
            ...emp,
            total_days: res.data.total_days,
            absent_days: res.data.absent_days,
            leave_days: res.data.leave_days,
            status: res.data.status,
            can_change_attendance: res.data.summary?.paybill_info?.can_change_attendance ?? true
          };
        }
        return emp;
      }));

      // Update card metrics
      setSummary(res.data.summary);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to save absentee records');
    } finally {
      setSaving(false);
    }
  };

  const getDayTypeColor = (day) => {
    if (day.is_on_leave) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (day.existing_absent) return 'bg-red-100 text-red-800 border-red-200';
    
    const isSelected = selectedDates.has(day.date);
    if (isSelected) return 'bg-red-200 text-red-900 border-red-300';
    
    if (previewData && previewData.bridged_dates && previewData.bridged_dates.includes(day.date)) {
      return 'bg-red-100 text-red-800 border-red-200 border-dashed border-2';
    }
    
    switch (day.day_type) {
      case 'WORKING_DAY': return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200 cursor-pointer';
      case 'HOLIDAY': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'WEEK_OFF': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'HOLIDAY_ON_WEEK_OFF': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-400 border-gray-200';
    }
  };

  const getDayTypeLabel = (day) => {
    if (day.is_on_leave) return 'Leave';
    if (day.existing_absent) return day.existing_absent.status === 'AUTO_BRIDGED' ? 'Bridged' : 'Absent';
    if (selectedDates.has(day.date)) return 'Absent';
    if (previewData && previewData.bridged_dates && previewData.bridged_dates.includes(day.date)) return 'Bridged';
    
    switch (day.day_type) {
      case 'WORKING_DAY': return 'Present';
      case 'HOLIDAY': return 'Holiday';
      case 'WEEK_OFF': return 'Week Off';
      case 'HOLIDAY_ON_WEEK_OFF': return 'Holiday';
      default: return '';
    }
  };

  const attendancePeriod = summary ? {
    from: formatDate(summary.attendance_from),
    to: formatDate(summary.attendance_to)
  } : null;

  return (
    <div className="space-y-6 p-6 bg-gray-50/50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mark Absentee</h1>
          <p className="text-sm text-gray-500">Manage employee absences for payroll attendance period</p>
        </div>
      </div>

      {!showModal && error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 flex justify-between items-center border border-red-200">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {successMsg && (
        <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700 flex justify-between items-center border border-green-200">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payroll Month</label>
            <select
              value={payrollMonth}
              onChange={(e) => setPayrollMonth(parseInt(e.target.value))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString('en', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
            <select
              value={payrollYear}
              onChange={(e) => setPayrollYear(parseInt(e.target.value))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[2025, 2026, 2027, 2028].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchSummary}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {loading ? 'Loading...' : 'Load Summary'}
          </button>
        </div>

        {attendancePeriod && (
          <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
            <span>Attendance Period:</span>
            <span className="font-semibold">{attendancePeriod.from}</span>
            <span>–</span>
            <span className="font-semibold">{attendancePeriod.to}</span>
          </div>
        )}

        {/* Paybill Status Banner */}
        {summary?.paybill_info && (
          <div className={`mt-4 p-3 rounded-lg border text-sm flex flex-wrap items-center justify-between gap-2 ${
            summary.paybill_info.is_locked
              ? 'bg-amber-50 border-amber-300 text-amber-900'
              : summary.paybill_info.has_paybill
                ? 'bg-blue-50 border-blue-200 text-blue-900'
                : 'bg-emerald-50 border-emerald-200 text-emerald-900'
          }`}>
            <div className="flex items-center space-x-2">
              <span className="font-semibold">Paybill Status:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                summary.paybill_info.is_locked
                  ? 'bg-amber-200 text-amber-900'
                  : summary.paybill_info.has_paybill
                    ? 'bg-blue-200 text-blue-900'
                    : 'bg-emerald-200 text-emerald-900'
              }`}>
                {summary.paybill_info.status}
              </span>
              <span className="text-xs text-gray-600">({summary.paybill_info.message})</span>
            </div>
            {summary.paybill_info.is_locked && (
              <span className="text-xs font-bold text-red-600 flex items-center gap-1">
                <ShieldAlert className="w-4 h-4" /> Locked Against Changes
              </span>
            )}
          </div>
        )}
      </div>

      {/* Mark Absent Button */}
      {hasPermission('leave:absence:mark') && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => {
              setIsCorrectionMode(false);
              setShowModal(true);
              setModalStep(1);
              setSelectedEmployee(null);
              setSelectedDates(new Set());
              setPreviewData(null);
              setReason('');
              setRemarks('');
            }}
            disabled={summary?.paybill_info?.is_locked}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            title={summary?.paybill_info?.is_locked ? "Attendance is locked because paybill is approved" : "Mark Absent"}
          >
            <Plus className="w-4 h-4" />
            <span>Mark Absent</span>
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Department</div>
                <div className="font-bold text-gray-900">{summary.department_name}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Employees</div>
                <div className="font-bold text-2xl text-gray-900">{summary.total_employees}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Days</div>
                <div className="font-bold text-2xl text-gray-900">{summary.total_days}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">With Absence</div>
                <div className="font-bold text-2xl text-gray-900">{summary.employees_with_absence}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">With Leave</div>
                <div className="font-bold text-2xl text-gray-900">{summary.employees_with_leave}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Overview Table */}
      {overview.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Employee Overview</h2>
            {selectedBulkIds.size > 0 && (
              <button
                onClick={handleBulkComplete}
                disabled={summary?.paybill_info?.is_locked || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition disabled:opacity-50"
              >
                Mark Selected as Complete ({selectedBulkIds.size})
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={allPendingSelected}
                      onChange={handleSelectAllPending}
                      disabled={pendingEmployees.length === 0}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3">Employee Code</th>
                  <th className="px-4 py-3">Employee Name</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Total Days</th>
                  <th className="px-4 py-3">Absent Days</th>
                  <th className="px-4 py-3">Leave Days</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {overview.map((emp) => (
                  <tr key={emp.employee_id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 w-12">
                      <input
                        type="checkbox"
                        checked={selectedBulkIds.has(emp.employee_id)}
                        onChange={() => handleToggleBulkSelect(emp.employee_id)}
                        disabled={emp.status !== 'Pending'}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">{emp.employee_code}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{emp.employee_name}</td>
                    <td className="px-4 py-3">{emp.designation}</td>
                    <td className="px-4 py-3 text-green-600 font-medium">{emp.total_days}</td>
                    <td className="px-4 py-3 text-red-600 font-medium">{emp.absent_days}</td>
                    <td className="px-4 py-3 text-blue-600 font-medium">{emp.leave_days}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        emp.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {emp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {hasPermission('leave:absence:check') && (
                          <button
                            onClick={() => {
                              setIsCorrectionMode(false);
                              handleOpenEditModal(emp);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View/Edit
                          </button>
                        )}
                        {/* Change Attendance Button */}
                        {emp.status === 'Complete' && emp.can_change_attendance && (
                          <button
                            onClick={() => handleReopenAttendance(emp)}
                            disabled={reopeningId === emp.employee_id || loading}
                            className="px-2.5 py-1 text-xs font-semibold rounded-md bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100 disabled:opacity-50 transition"
                            title="Reopen attendance for correction while paybill is in Draft"
                          >
                            {reopeningId === emp.employee_id ? 'Reopening...' : 'Change Attendance'}
                          </button>
                        )}
                        {emp.status === 'Complete' && !emp.can_change_attendance && (
                          <span
                            className="text-xs text-gray-400 italic px-1"
                            title={summary?.paybill_info?.message || "Locked: Paybill is approved"}
                          >
                            Locked
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mark Absent Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex justify-between items-center z-10">
              <div className="flex items-center space-x-3">
                <h3 className="font-bold text-xl text-gray-900">
                  {isCorrectionMode ? 'Change Attendance (Correction)' : 'Mark Absent'}
                </h3>
                {isCorrectionMode && (
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                    Draft Reopened
                  </span>
                )}
              </div>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-700 flex justify-between items-center border border-red-200">
                  <span>{error}</span>
                  <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
                </div>
              )}
              {modalStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search Employee</label>
                    <div className="relative">
                      <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name or employee code..."
                        value={employeeSearch}
                        onChange={(e) => handleEmployeeSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {employeeResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {employeeResults.map((emp) => (
                        <button
                          key={emp.id}
                          onClick={() => handleSelectEmployee(emp)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition flex items-center justify-between"
                        >
                          <div>
                            <div className="font-medium text-gray-900">{emp.full_name || `${emp.first_name} ${emp.last_name}`}</div>
                            <div className="text-sm text-gray-500">{emp.emp_code}</div>
                          </div>
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {modalStep === 2 && selectedEmployee && attendanceData && (
                <div className="space-y-6">
                  {/* Correction Mode Notice */}
                  {isCorrectionMode && (
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                      <div className="flex items-start">
                        <RefreshCw className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="ml-3">
                          <p className="text-sm font-semibold text-amber-800">
                            Attendance Correction Mode
                          </p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            Previous attendance records and selections have been preserved. Adjust only the dates requiring correction. Saving will update the review status to Complete and record an audit log entry.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Employee Info */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Employee Code:</span>
                        <span className="ml-2 font-medium">{attendanceData.employee_code}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <span className="ml-2 font-medium">{selectedEmployee.first_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Department:</span>
                        <span className="ml-2 font-medium">{attendanceData.department}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Designation:</span>
                        <span className="ml-2 font-medium">{attendanceData.designation}</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Payroll Period: {formatDate(attendanceData.attendance_from)} – {formatDate(attendanceData.attendance_to)}
                    </div>
                  </div>

                  {/* Payroll Lock Warning */}
                  {attendanceData.is_locked && (
                    <div className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800 border border-amber-200">
                      This payroll period is approved or paid and is read-only.
                    </div>
                  )}

                  {/* Marking Mode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Marking Mode</label>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          value="calendar"
                          checked={markingMode === 'calendar'}
                          onChange={() => setMarkingMode('calendar')}
                          disabled={attendanceData.is_locked}
                          className="text-blue-600"
                        />
                        <span>Calendar Selection</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          value="range"
                          checked={markingMode === 'range'}
                          onChange={() => setMarkingMode('range')}
                          disabled={attendanceData.is_locked}
                          className="text-blue-600"
                        />
                        <span>Date Range</span>
                      </label>
                    </div>
                  </div>

                  {/* Date Range Mode */}
                  {markingMode === 'range' && (
                    <div className="flex flex-wrap items-end gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                        <input
                          type="date"
                          value={rangeFrom}
                          min={attendanceData.attendance_from}
                          max={attendanceData.attendance_to}
                          onChange={(e) => setRangeFrom(e.target.value)}
                          disabled={attendanceData.is_locked}
                          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                        <input
                          type="date"
                          value={rangeTo}
                          min={attendanceData.attendance_from}
                          max={attendanceData.attendance_to}
                          onChange={(e) => setRangeTo(e.target.value)}
                          disabled={attendanceData.is_locked}
                          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleDateRangeSelect}
                        disabled={attendanceData.is_locked}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
                      >
                        Select Range
                      </button>
                      {selectedDates.size > 0 && (
                        <div className="text-xs text-blue-600 font-medium pb-2">
                          {selectedDates.size} date{selectedDates.size > 1 ? 's' : ''} currently selected
                        </div>
                      )}
                    </div>
                  )}

                  {/* Calendar Mode */}
                  {markingMode === 'calendar' && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-7 bg-gray-100 text-xs font-bold text-gray-700 uppercase tracking-wider">
                        {[ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat','Sun'].map((day) => (
                        <div key={day} className="px-2 py-2 text-center border-r border-gray-200 last:border-r-0">{day}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7">
                        {(() => {
                          // Transform calendar_grid into proper weekday-positioned grid
                          if (!attendanceData.calendar_grid || attendanceData.calendar_grid.length === 0) {
                            return null;
                          }
                          
                          // Create a map of date -> day object for quick lookup
                          const dayMap = {};
                          attendanceData.calendar_grid.forEach(day => {
                            dayMap[day.date] = day;
                          });
                          
                          // Get the date range
                          const firstDate = new Date(attendanceData.calendar_grid[0].date);
                          const lastDate = new Date(attendanceData.calendar_grid[attendanceData.calendar_grid.length - 1].date);
                          
                          // Calculate the start of the first week (Monday before or on first date)
                          const firstDayOfWeek = firstDate.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
                          const mondayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Convert to 0=Mon, ..., 6=Sun
                          const startDate = new Date(firstDate);
                          startDate.setDate(startDate.getDate() - mondayOffset);
                          
                          // Calculate the end of the last week (Sunday after or on last date)
                          const lastDayOfWeek = lastDate.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
                          const sundayOffset = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek; // Days until Sunday
                          const endDate = new Date(lastDate);
                          endDate.setDate(endDate.getDate() + sundayOffset);
                          
                          // Generate all dates in the grid range
                          const gridCells = [];
                          const currentDate = new Date(startDate);
                          
                          while (currentDate <= endDate) {
                            const yyyy = currentDate.getFullYear();
                            const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
                            const dd = String(currentDate.getDate()).padStart(2, '0');
                            const dateStr = `${yyyy}-${mm}-${dd}`;
                            
                            const dayData = dayMap[dateStr];
                            if (dayData) {
                              gridCells.push(dayData);
                            } else {
                              // Blank cell for dates outside the payroll period
                              gridCells.push(null);
                            }
                            
                            currentDate.setDate(currentDate.getDate() + 1);
                          }
                          
                          return gridCells.map((day, idx) => {
                            if (!day) {
                              // Blank cell
                              return (
                                <div
                                  key={idx}
                                  className="p-2 border-r border-b border-gray-100 min-h-[60px] bg-gray-50"
                                />
                              );
                            }
                            
                            return (
                              <div
                                key={idx}
                                onClick={() => handleDateClick(day)}
                                className={`p-2 border-r border-b border-gray-100 min-h-[60px] ${getDayTypeColor(day)}`}
                                title={`${day.date} - ${getDayTypeLabel(day)}`}
                              >
                                <div className="text-xs font-semibold">{new Date(day.date).getDate()}</div>
                                <div className="text-[9px] mt-0.5 opacity-75">{day.day_of_week.slice(0, 3)}</div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                  {/* Bridge Rule Toggle */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="bridgeRule"
                      checked={applyBridgeRule}
                      onChange={(e) => setApplyBridgeRule(e.target.checked)}
                      disabled={attendanceData.is_locked}
                      className="text-blue-600 rounded"
                    />
                    <label htmlFor="bridgeRule" className="text-sm text-gray-700">
                      Include Weekly Off Between Selected Dates (automatically mark weekends/holidays between absent days)
                    </label>
                  </div>

                  {/* Reason Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason {selectedDates.size > 0 ? '*' : ''}</label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={attendanceData.is_locked}
                      placeholder={selectedDates.size > 0 ? "Enter reason for marking absent" : "No absences selected"}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </div>

                  {/* Remarks Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      disabled={attendanceData.is_locked}
                      placeholder="Additional remarks (optional)"
                      rows="2"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  </div>

                  {/* Actions: Preview & Save */}
                  {!previewData ? (
                    <div className="flex space-x-3 pt-2">
                      {selectedDates.size > 0 && (
                        <button
                          onClick={handlePreview}
                          disabled={attendanceData.is_locked || saving}
                          className="flex-1 px-4 py-3 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-200 transition disabled:opacity-50"
                        >
                          Preview Before Save
                        </button>
                      )}
                      <button
                        onClick={handleSave}
                        disabled={attendanceData.is_locked || saving || (selectedDates.size > 0 && !reason)}
                        className={`px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50 ${selectedDates.size > 0 ? 'flex-1' : 'w-full'}`}
                      >
                        {saving ? 'Saving...' : (selectedDates.size === 0 ? 'Save (Mark All Present & Complete)' : 'Save & Mark Complete')}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-amber-50 rounded-lg p-4 space-y-3 border border-amber-200">
                      <h4 className="font-semibold text-gray-900">Preview Summary</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-gray-500">Manual Days</div>
                          <div className="font-bold text-xl text-gray-900">{previewData.manual_days}</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-gray-500">Bridge Days</div>
                          <div className="font-bold text-xl text-gray-900">{previewData.bridge_days}</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-gray-500">Total Absent Days</div>
                          <div className="font-bold text-xl text-gray-900">{previewData.total_days}</div>
                        </div>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => setPreviewData(null)}
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                        >
                          Back to Edit
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={attendanceData.is_locked || saving}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Confirm & Save Attendance'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkAbsenteeManagement;
